import { BRAND_MODES } from '../branding/brands';

/**
 * Whom people see: Amigo on FoodAmigos' own dashboard, "the assistant" on a partner's (or a neutral) one, which
 * never mentions FoodAmigos. A key, not the words: each piece of copy has its own wording per name
 * ("Ask Amigo" / "Assistant", "Close Amigo" / "Close the assistant").
 */
export const PLATFORM_AGENT_NAMES = Object.freeze({
  Amigo: 'amigo',
  Assistant: 'assistant',
});

/**
 * The panel's id (QA79): its labelled aside has it, and the button's aria-controls names it. One constant, since the
 * panel is loaded apart from the button.
 */
export const PLATFORM_AGENT_PANEL_ID = 'platform-agent-panel';

export function platformAgentName(brand) {
  return brand?.mode === BRAND_MODES.FOOD_AMIGOS ? PLATFORM_AGENT_NAMES.Amigo : PLATFORM_AGENT_NAMES.Assistant;
}

/** The key of a chat's subject (contracts §0), e.g. `franchise:12`; null without one. */
export function platformAgentSubjectKey(subject) {
  if (!subject?.type || subject.id == null) {
    return null;
  }

  return `${subject.type}:${subject.id}`;
}

/** Whether eve's health check says the agent can take a chat. */
export function isPlatformAgentHealthy(health) {
  return health?.ok === true;
}

/** The button's count from the agent's unread answer (contracts §6 `{ unread, chats }`): 0 for anything but a count. */
export function platformAgentUnreadCount(answer) {
  const count = answer?.unread;

  return Number.isInteger(count) && count > 0 ? count : 0;
}

/** Whether the agent's unread answer (contracts §6 `{ unread, chats }`) lists the chat `id` with answers to read. */
export function platformAgentChatHasNews(answer, id) {
  if (!id || !Array.isArray(answer?.chats)) {
    return false;
  }

  return answer.chats.some((chat) => chat?.id === id && Number.isInteger(chat.unread) && chat.unread > 0);
}

/** The chats the button's news lists with answers to read, newest first as the agent lists them (QA45). */
export function platformAgentNewsChats(answer) {
  if (!Array.isArray(answer?.chats)) {
    return [];
  }

  return answer.chats
    .filter((chat) => chat?.id && Number.isInteger(chat.unread) && chat.unread > 0)
    .map(({ id }) => id);
}

/** The newest chat the button's news lists (the agent lists them newest first), or null. */
export function platformAgentNewestNews(answer) {
  return platformAgentNewsChats(answer)[0] ?? null;
}

/**
 * Where the panel sits (spec Responsive, QA78), at Tailwind's default breakpoints: full screen under 640 px; from
 * 640 px it lies over the page on the right, full height, with the design system's float shadow and no dimming, so the
 * page keeps its width and stays usable; from 1280 px it sits beside the page, which Layout narrows by the panel's
 * 400 px (PLATFORM_AGENT_PAGE_BESIDE), and needs no shadow.
 */
export const PLATFORM_AGENT_PANEL_LAYOUT =
  'fixed inset-y-0 right-0 z-[60] w-full bg-white sm:w-[400px] sm:border-0 sm:border-l sm:border-solid sm:[box-shadow:var(--fa-shadow-float)] xl:[box-shadow:none]';

/** The page's room for the open panel: only from 1280 px, where the panel sits beside it (QA78). */
export const PLATFORM_AGENT_PAGE_BESIDE = 'xl:mr-[400px]';
